import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { isConflictError } from '@jira/polaris__lib-errors/src/isPermissionError.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import { isClientFetchError } from '@atlassian/jira-fetch/src/index.tsx';
import { performUpdateIssueType } from './performUpdateIssueType.tsx';
import type { IssueTypeModifiableProps, State } from './IssueType.types.tsx';
import { isUpdatingRemovedIssueTypeError } from './isUpdatingRemovedIssueTypeError.tsx';

type IssueTypeId = string;
type ProjectId = string;

type UpdateRequest = IssueTypeModifiableProps & {
	avatarUrl?: string;
};

export const updateIssueType =
	(issueTypeId: IssueTypeId, projectId: ProjectId, update: UpdateRequest) =>
	async ({ setState, getState }: StoreActionApi<State>): Promise<void> => {
		experience.issueTypes.issueTypeUpdate.start();

		const state = getState();
		const currentIssueType = state.issueTypesById[issueTypeId];

		if (!currentIssueType) {
			return;
		}

		setState({
			issueTypesById: {
				...state.issueTypesById,
				[issueTypeId]: {
					...currentIssueType,
					...update,
				},
			},
		});

		try {
			await performUpdateIssueType(issueTypeId, projectId, {
				name: update.name,
				description: update.description,
				avatarId: Number(update.avatarId),
			});

			experience.issueTypes.issueTypeUpdate.success();
		} catch (maybeError) {
			const error =
				maybeError instanceof Error ? maybeError : new Error('Error while updating issue type');

			if (
				isClientFetchError(error) ||
				isConflictError(error) ||
				isUpdatingRemovedIssueTypeError(error)
			) {
				experience.issueTypes.issueTypeUpdate.abort();
			} else {
				experience.issueTypes.issueTypeUpdate.failure(error);
			}

			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.error.controllers.issue-types.actions.update-issue-type',
				'Failed to update issue type',
				error,
			);

			setState(state);
			throw error;
		}
	};
